/**
 * Pixelation pass (WebGPU / TSL).
 */

import { clamp, colorToDirection, convertToTexture, dot, float, floor, max, screenSize, smoothstep, step, uniform, uv, vec2 } from 'three/tsl';

import { type EffectContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, type PixelationEffectConfig, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';

import { WebGPUOnlyEffect } from './WebGPUOnlyEffectBase.js';

const EDGE_STRENGTH_EPSILON = 0.000001;

type PixelationRuntimeNode = WebGPUEffectNode & {
  pixelationRuntime: {
    pixelSize: { value: number };
    normalEdgeStrength: { value: number };
    depthEdgeStrength: { value: number };
  };
};

export class PixelationEffect extends WebGPUOnlyEffect<PixelationEffectConfig> {
  override readonly type = PostProcessPass.Pixelation;
  override readonly order = PostProcessRenderOrder.Pixelation;

  override getDefaultConfig(): PixelationEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.Pixelation] };
  }

  override createWebGPUNode(config: PixelationEffectConfig, _context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const pixelSizeNode = uniform(config.pixelSize ?? defaults.pixelSize ?? 6);
    const normalEdgeStrengthNode = uniform(config.normalEdgeStrength ?? defaults.normalEdgeStrength ?? 0.3);
    const depthEdgeStrengthNode = uniform(config.depthEdgeStrength ?? defaults.depthEdgeStrength ?? 0.4);
    const enableNormalEdges = (config.normalEdgeStrength ?? defaults.normalEdgeStrength ?? 0.3) > EDGE_STRENGTH_EPSILON;
    const enableDepthEdges = (config.depthEdgeStrength ?? defaults.depthEdgeStrength ?? 0.4) > EDGE_STRENGTH_EPSILON;
    const hasAnyEdges = enableNormalEdges || enableDepthEdges;

    const effectNode: WebGPUEffectNode = {
      node: null as any,
      needsMRT: enableNormalEdges,
      mrtOutputs: enableNormalEdges ? ['normal'] : undefined,
      compose: (outputNode, ctx) => {
        const colorTexture = convertToTexture(outputNode as any);
        const depthTexture = convertToTexture(ctx.depthNode as any);

        const pixelSize = max(float(1), pixelSizeNode);
        const resolution = screenSize.xy;
        const pixelGrid = resolution.div(pixelSize);
        const snappedUV = floor(uv().mul(pixelGrid)).add(0.5).div(pixelGrid);
        const texelOffset = vec2(pixelSize).div(resolution);
        const offsetX = vec2(texelOffset.x, 0);
        const offsetY = vec2(0, texelOffset.y);

        const baseColor = colorTexture.sample(snappedUV);
        if (!hasAnyEdges) {
          return baseColor as any;
        }

        let normalEdge: any = float(0.0);
        if (enableNormalEdges) {
          const normalTextureNode = ctx.getMRTTexture('normal');
          if (normalTextureNode) {
            const normalTexture = convertToTexture(normalTextureNode as any);
            const centerNormal = colorToDirection(normalTexture.sample(snappedUV).rgb).normalize();
            const normalRight = colorToDirection(normalTexture.sample(snappedUV.add(offsetX)).rgb).normalize();
            const normalLeft = colorToDirection(normalTexture.sample(snappedUV.sub(offsetX)).rgb).normalize();
            const normalUp = colorToDirection(normalTexture.sample(snappedUV.add(offsetY)).rgb).normalize();
            const normalDown = colorToDirection(normalTexture.sample(snappedUV.sub(offsetY)).rgb).normalize();
            normalEdge = step(
              float(0.1),
              float(1)
                .sub(dot(centerNormal, normalRight))
                .add(float(1).sub(dot(centerNormal, normalLeft)))
                .add(float(1).sub(dot(centerNormal, normalUp)))
                .add(float(1).sub(dot(centerNormal, normalDown)))
            );
          }
        }
        let depthEdge: any = float(0.0);
        if (enableDepthEdges) {
          const depth = depthTexture.sample(snappedUV).r;
          const depthDiff = clamp(depthTexture.sample(snappedUV.add(offsetX)).r.sub(depth), 0.0, 1.0)
            .add(clamp(depthTexture.sample(snappedUV.sub(offsetX)).r.sub(depth), 0.0, 1.0))
            .add(clamp(depthTexture.sample(snappedUV.add(offsetY)).r.sub(depth), 0.0, 1.0))
            .add(clamp(depthTexture.sample(snappedUV.sub(offsetY)).r.sub(depth), 0.0, 1.0));
          depthEdge = smoothstep(float(0.01), float(0.02), depthDiff);
        }

        const edgeStrength = float(1)
          .sub(depthEdge.mul(depthEdgeStrengthNode))
          .mul(float(1).add(normalEdge.mul(normalEdgeStrengthNode)));

        return baseColor.mul(edgeStrength) as any;
      },
    };
    (effectNode as PixelationRuntimeNode).pixelationRuntime = {
      pixelSize: pixelSizeNode,
      normalEdgeStrength: normalEdgeStrengthNode,
      depthEdgeStrength: depthEdgeStrengthNode,
    };
    return effectNode;
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: PixelationEffectConfig): void {
    const runtimeNode = node as PixelationRuntimeNode;
    if (!runtimeNode.pixelationRuntime) return;
    const defaults = this.getDefaultConfig();
    runtimeNode.pixelationRuntime.pixelSize.value = config.pixelSize ?? defaults.pixelSize ?? 6;
    runtimeNode.pixelationRuntime.normalEdgeStrength.value = config.normalEdgeStrength ?? defaults.normalEdgeStrength ?? 0.3;
    runtimeNode.pixelationRuntime.depthEdgeStrength.value = config.depthEdgeStrength ?? defaults.depthEdgeStrength ?? 0.4;
  }

  override requiresRebuild(oldConfig: PixelationEffectConfig, newConfig: PixelationEffectConfig): boolean {
    const defaults = this.getDefaultConfig();
    const oldNeedsNormalMRT = (oldConfig.normalEdgeStrength ?? defaults.normalEdgeStrength ?? 0.3) > EDGE_STRENGTH_EPSILON;
    const newNeedsNormalMRT = (newConfig.normalEdgeStrength ?? defaults.normalEdgeStrength ?? 0.3) > EDGE_STRENGTH_EPSILON;
    const oldNeedsDepthEdges = (oldConfig.depthEdgeStrength ?? defaults.depthEdgeStrength ?? 0.4) > EDGE_STRENGTH_EPSILON;
    const newNeedsDepthEdges = (newConfig.depthEdgeStrength ?? defaults.depthEdgeStrength ?? 0.4) > EDGE_STRENGTH_EPSILON;

    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.order !== newConfig.order ||
      oldConfig.type !== newConfig.type ||
      oldConfig.id !== newConfig.id ||
      oldNeedsNormalMRT !== newNeedsNormalMRT ||
      oldNeedsDepthEdges !== newNeedsDepthEdges
    );
  }
}
